import { z } from 'zod';
import { EXPENSE_APPROVAL_STATUSES, PAYMENT_METHODS } from '../enums.js';
import { currencySchema } from './organization.js';
import { optionalText, paginationQuerySchema, toPatchSchema, uuidSchema } from './common.js';
import type { ExpenseApprovalStatus } from '../enums.js';
import {
  businessManagerFilterSchema,
  type AttachmentRef,
  type BusinessManagerRef,
  type RecordRef,
} from './records.js';

/**
 * Expenses
 * ========
 * What an organisation's own people spent, not what a customer owes it — the
 * mirror image of a payment received. A receipt, a category, an amount and who
 * paid for it, with the same treatment every other document in WHNCMS gets:
 * ownership scoping, an audit trail, custom fields and a system picklist for
 * the vocabulary an admin edits (`category`, `paidFromAccount`, `verticals`).
 */

const money = z.coerce.number().positive('Enter an amount greater than zero').max(1e15);

/**
 * One line of an itemised expense ("Food 1,000", "Fuel 1,000"). With lines, the
 * expense's amount is their sum and is worked out by the server.
 */
export const expenseItemSchema = z.object({
  category: z.string().trim().min(1, 'Choose a category').max(80),
  description: optionalText(500),
  amount: money,
});
export type ExpenseItemInput = z.infer<typeof expenseItemSchema>;

export const createExpenseSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  expenseDate: z.iso.date(),
  /** Ignored when `items` is given: the total is then the sum of the lines. */
  amount: money.optional(),
  /** Itemise the expense. Replaced wholesale on update; an empty list un-itemises it. */
  items: z.array(expenseItemSchema).max(50).optional(),
  currency: currencySchema.optional(),
  /** What the expense was for. A system picklist; an admin edits the options. */
  category: z.string().trim().min(1).max(80).nullish(),
  /** Which of the organisation's own accounts paid for it. Also a system picklist. */
  paidFromAccount: z.string().trim().min(1).max(80).nullish(),
  /**
   * The real Banking-module account this was paid from, if any — independent
   * of `paidFromAccount` above, which stays for backward compatibility.
   */
  bankAccountId: uuidSchema.nullish(),
  paymentMethod: z.enum(PAYMENT_METHODS).default('OTHER'),
  vendor: optionalText(200),
  notes: optionalText(2000),
  verticals: z.array(z.string().trim().min(1).max(80)).default([]),
  accountId: uuidSchema.nullish(),
  contactId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

/** Either an amount or at least one line says what was spent. */
export function expenseHasAmount(input: { amount?: number | undefined; items?: unknown[] | undefined }): boolean {
  return (input.items?.length ?? 0) > 0 || input.amount !== undefined;
}

export const updateExpenseSchema = toPatchSchema(createExpenseSchema);
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

export const EXPENSE_SORT_FIELDS = ['expenseDate', 'createdAt', 'amount'] as const;

export const listExpensesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(EXPENSE_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  category: z.string().trim().max(80).optional(),
  vertical: z.string().trim().max(80).optional(),
  paidFromAccount: z.string().trim().max(80).optional(),
  accountId: uuidSchema.optional(),
  currency: z.string().trim().length(3).optional(),
  /** Inclusive, on the day the expense was incurred. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  minAmount: z.coerce.number().optional(),
  maxAmount: z.coerce.number().optional(),
  /** Only expenses at this step of the approval. */
  approvalStatus: z.enum(EXPENSE_APPROVAL_STATUSES).optional(),
});
export type ListExpensesQuery = z.infer<typeof listExpensesQuerySchema>;

export interface ExpenseSummary {
  id: string;
  name: string;
  expenseDate: string;
  amount: string;
  currency: string;
  /** The single category, or null when the lines span several (see `itemCount`). */
  category: string | null;
  /** How many lines the expense is itemised into; 0 when it is a single amount. */
  itemCount: number;
  paidFromAccount: string | null;
  /** The real Banking-module account this was paid from, if the newer selector was used. */
  bankAccount: RecordRef | null;
  paymentMethod: (typeof PAYMENT_METHODS)[number];
  vendor: string | null;
  /** The vendor record this was billed by, when it came from a purchase order. */
  vendorRecord: RecordRef | null;
  /** The purchase order it was created from, if any. */
  purchaseOrder: RecordRef | null;
  verticals: string[];
  account: RecordRef | null;
  contact: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  receipt: AttachmentRef | null;
  /** Where the expense is in its two-person approval. Only an approved one is in the bank. */
  approvalStatus: ExpenseApprovalStatus;
  createdAt: string;
  updatedAt: string;
}

/** One signature on an expense: who, and when. */
export interface ExpenseSignoff {
  by: RecordRef | null;
  at: string;
}

export interface ExpenseApproval {
  status: ExpenseApprovalStatus;
  /** Legacy first signature, from expenses approved before approval became one step. */
  accountant: ExpenseSignoff | null;
  /** Who approved the expense, and when. Approval posts the expense to the bank. */
  admin: ExpenseSignoff | null;
  /** Whether the signed-in person may approve or reject it now: an administrator, or the assigned approver. */
  canDecide: boolean;
  rejection: (ExpenseSignoff & { reason: string }) | null;
}

export const rejectExpenseSchema = z.object({
  reason: z.string().trim().min(3, 'Say why, in a few words').max(500),
});
export type RejectExpenseInput = z.infer<typeof rejectExpenseSchema>;

export interface ExpenseItemView {
  id: string;
  category: string | null;
  description: string | null;
  amount: string;
}

export interface ExpenseDetail extends ExpenseSummary {
  items: ExpenseItemView[];
  notes: string | null;
  approval: ExpenseApproval;
  customFields: Record<string, unknown>;
}

/** The categories, accounts and verticals an admin has configured for expenses. */
export interface ExpenseOptions {
  categories: { value: string; label: string }[];
  paidFromAccounts: { value: string; label: string }[];
  verticals: { value: string; label: string }[];
}
